import axios from "axios";
import { _adr, _ckey } from "../address";
import { getLocalStorage } from "./services";
import { persistor } from "../reducer/store";

// "ورود به عنوان": the impersonation token lives only in this tab's sessionStorage; the original token in
// localStorage is never touched, so closing the tab or exiting always falls back to the operator's own session.
const KEY = "_impersonation";

export const getImpersonation = () => {
	try {
		return JSON.parse(sessionStorage.getItem(KEY));
	} catch {
		return null;
	}
};

export const isImpersonating = () => !!getImpersonation()?.token;

// the token every API call should carry right now
export const activeToken = () => getImpersonation()?.token || getLocalStorage(_ckey.token);

const API = process.env.REACT_APP_API_KEY + "/dealer/Impersonation";

// envelope keys are PascalCase but Result fields are camelCase (token, isImpersonating, items…); normalize to PascalCase
export const toPascal = (v) =>
	Array.isArray(v)
		? v.map(toPascal)
		: v && typeof v === "object"
		? Object.fromEntries(Object.entries(v).map(([k, val]) => [k.charAt(0).toUpperCase() + k.slice(1), toPascal(val)]))
		: v;

// standard dealer envelope { Code, HasError, Message, Result }: ok = !HasError, data = Result.
// Called with an explicit token (original vs impersonation), which QueryAPI cannot do.
const call = async (method, path, body, token) => {
	let status;
	let envelope;
	try {
		const res = await axios({
			method,
			url: API + path,
			data: body,
			timeout: 30000,
			headers: { Authorization: "Bearer " + token, "Content-Type": "application/json" },
		});
		status = res.status;
		envelope = res.data;
	} catch (error) {
		status = error?.response?.status;
		envelope = error?.response?.data;
	}
	const ok = status >= 200 && status < 300 && envelope?.HasError === false;
	return {
		ok,
		status,
		code: envelope?.Code,
		data: toPascal(envelope?.Result ?? null),
		message: ok ? null : envelope?.Message || "سرویس درحال حاضر در دسترس نمی‌باشد",
	};
};

const originalToken = () => getLocalStorage(_ckey.token);

export const searchDealer = (username) => call("post", "/Search", { username }, originalToken());

export const queryImpersonationStatus = () => call("get", "/Status", undefined, activeToken());

// the persisted current user (redux, localStorage) is shared by all tabs, so after every identity switch the
// layout refetches it with activeToken(); this flag tells a tab its stored user may belong to someone else
const USER_STALE_KEY = "_impersonation_user_stale";
export const isUserStale = () => isImpersonating() || localStorage.getItem(USER_STALE_KEY) === "1";
export const clearUserStale = () => !isImpersonating() && localStorage.removeItem(USER_STALE_KEY);

const goHome = async () => {
	localStorage.setItem(USER_STALE_KEY, "1");
	await persistor.flush();
	// full reload so the menu, roles and every cached screen are rebuilt for the new identity
	window.location.href = _adr.Root + _adr.Home;
};

export const startImpersonation = async (username) => {
	const res = await call("post", "/Start", { username }, originalToken());
	const data = res.data ?? {};
	if (!res.ok || !data.Token) return { ok: false, message: res.message || "شروع ورود به عنوان ممکن نشد" };
	sessionStorage.setItem(
		KEY,
		JSON.stringify({
			token: data.Token,
			expiresAt: data.ExpiresAt,
			sessionId: data.SessionId,
			dealerUsername: username,
		}),
	);
	await goHome();
	return { ok: true };
};

// ends the session in this tab and returns to the operator; callServer=false when the token is already dead (401/expired)
export const exitImpersonation = async ({ callServer = true } = {}) => {
	const imp = getImpersonation();
	if (!imp) return;
	if (callServer) await call("post", "/Exit", undefined, imp.token);
	sessionStorage.removeItem(KEY);
	await goHome();
};

// operations the operator must not perform while inside a dealer's panel (requirement doc §3)
const BLOCKED_PATHS = [
	_adr.SimCardPurchase.Index + _adr.SimCardPurchase.Kit,
	_adr.SimCardPurchase.Index + _adr.SimCardPurchase.Msisdn,
	_adr.SimCardPurchase.Index + _adr.SimCardPurchase.Profile,
	_adr.SimCardPurchase.Index + _adr.SimCardPurchase.PreFactor,
	_adr.SimCardPurchase.Index + _adr.SimCardPurchase.Document,
	_adr.SimCardPurchase.Index + _adr.SimCardPurchase.Final,
	...Object.values(_adr.SimCardPurchase.Esim).map((p) => _adr.SimCardPurchase.Index + p),
	_adr.SimCardPurchase.foreigners,
	_adr.SimCardReplacement.Index + _adr.SimCardReplacement.Replacement,
	_adr.DeliveryArea.Index + _adr.DeliveryArea.Delivery.Index,
	_adr.ChangeOwnerShip.Index + _adr.ChangeOwnerShip.OwnerShip.Index,
	_adr.Corporate.Index + _adr.Corporate.SimSwap.Index,
	_adr.Corporate.Index + _adr.Corporate.Ownership.Index,
	_adr.EPPurchase.Index + _adr.EPPurchase.Shop,
	_adr.DCPurchase.Index + _adr.DCPurchase.Shop,
	_adr.PayBill.Index + _adr.PayBill.Shop,
	_adr.Wallet.Index + _adr.Wallet.Withdraw.Index,
	_adr.SimLocked.Dealer.Index + _adr.SimLocked.Dealer.NewRequest,
	_adr.Bulks.Index,
	_adr.LoginAs.Index,
].map((p) => (_adr.Root + p).toLowerCase());

export const isBlockedPath = (pathname = "") => {
	const path = pathname.toLowerCase();
	return BLOCKED_PATHS.some((p) => path === p || path.startsWith(p + "/"));
};

const BLOCKED_MENU_KEYS = new Set([
	"menu01sub01",
	"menuEsimSale",
	"menu01sub03",
	"menu02",
	"menu03",
	"menu04",
	"menu06sub01",
	"menu07sub01",
	"paybillSub",
	"newRequest",
	"bulkDC",
	"menuLoginAs",
	"menuLoginAsLogs",
]);

// drops blocked entries and any parent left without children
export const filterImpersonationMenu = (items = []) =>
	items
		.filter((i) => i && !BLOCKED_MENU_KEYS.has(i.key))
		.map((i) => (i.children ? { ...i, children: filterImpersonationMenu(i.children) } : i))
		.filter((i) => !i.children || i.children.length);
